import { useLocation } from '@tanstack/react-router';

/**
 * Safe hook to get location, with fallback when used outside router context.
 * Returns a fallback location object if useLocation throws an error.
 *
 * Note: This uses a try-catch pattern around the hook call. While this is
 * not the typical React pattern, it works when the hook throws synchronously
 * during render (not as a hook rules violation).
 */
export const useSafeLocation = () => {
  try {
    // Attempt to use the location hook
    // This will throw if not within a router context
    return useLocation();
  } catch (error) {
    // Return fallback location when outside router context
    // This allows the component to work even when not in a router context
    // Use window.location as fallback if available
    const fallbackPathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    return {
      pathname: fallbackPathname,
      search: typeof window !== 'undefined' ? window.location.search : '',
      hash: typeof window !== 'undefined' ? window.location.hash : '',
      state: null,
    } as unknown as ReturnType<typeof useLocation>;
  }
};
